import { lazy, Suspense, useEffect, type CSSProperties } from "react";
import { BrandIcon } from "./BrandIcon";
import { GardenWordmark } from "./GardenWordmark";
import { GardenExplorer } from "./GardenExplorer";
import { Icon, useInView, type IconName } from "./shared";
import "./garden.css";
import "./product-demos.css";

const AppsShowcase = lazy(() => import("./demos"));
const alphaNotice =
  "An alpha experiment by Sauna. Expect bugs, instability, and resets. Your data may be deleted. Keep anything important elsewhere.";

function Brand() {
  return (
    <a className="g-brand garden-wordmark" href="/" aria-label="Garden by Sauna home">
      <GardenWordmark />
    </a>
  );
}
function Header({ docs = false }: { docs?: boolean }) {
  return (
    <header className="g-header g-container">
      <Brand />
      <nav aria-label="Main navigation">
        <a href={docs ? "/#together" : "#together"}>How it works</a>
        <a href={docs ? "/#apps" : "#apps"}>Apps</a>
        <a href="/docs" aria-current={docs ? "page" : undefined}>
          Docs
        </a>
        <a className="g-button g-nav-cta" href="/app">
          Try the alpha <Icon name="arrow" />
        </a>
      </nav>
    </header>
  );
}
function AlphaNotice() {
  return (
    <p className="g-alpha-notice">
      <span className="g-alpha-dot" />
      {alphaNotice}
    </p>
  );
}
function Footer() {
  return (
    <footer className="g-footer g-container">
      <Brand />
      <span>A little common ground for what comes next.</span>
      <a href="/docs">
        Docs <span className="g-coming-label">Coming soon</span>
      </a>
      <a href="https://sauna.ai" target="_blank" rel="noreferrer">
        Made by Sauna <Icon name="arrow" />
      </a>
    </footer>
  );
}
function Botanical({
  className = "",
  lazy = true,
}: {
  className?: string;
  lazy?: boolean;
}) {
  return (
    <img
      className={`g-botanical ${className}`}
      src="/garden/allium.webp"
      width="680"
      height="1020"
      alt=""
      loading={lazy ? "lazy" : "eager"}
      decoding="async"
    />
  );
}
function AppsSection() {
  const { ref, visited } = useInView<HTMLElement>("350px");
  return (
    <section
      className="g-apps-section"
      id="apps"
      aria-labelledby="apps-title"
      ref={ref}
    >
      <div className="g-container">
        <div className="g-apps-intro">
          <span className="g-eyebrow">ROOM TO BUILD. ROOM TO GROW.</span>
          <h2 id="apps-title">
            Plant the context.
            <br />
            <em>Watch something useful grow.</em>
          </h2>
          <p>
            Build the tools your team needs. Let them take care of the recurring
            work.
          </p>
          <span className="g-apps-botanical" aria-hidden="true">
            <Icon name="sprout" />
          </span>
        </div>
        <div className="g-lazy-demos">
          {visited ? (
            <Suspense
              fallback={
                <p className="g-demo-loading" role="status">
                  Growing the examples…
                </p>
              }
            >
              <AppsShowcase />
            </Suspense>
          ) : (
            <p className="g-demo-loading">
              Apps and automations, rooted in your shared context.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function IntegrationsSection() {
  const { ref, visible, visited } = useInView<HTMLElement>();
  return (
    <section
      className="g-integrations g-container"
      ref={ref}
      data-visible={visible}
      data-visited={visited}
      aria-labelledby="integrations-title"
    >
      <div className="g-integrations-copy">
        <span className="g-eyebrow">ROOTED IN THE TOOLS YOU ALREADY USE</span>
        <h2 id="integrations-title">
          3,000+ integrations.
          <br />
          <em>One shared advantage.</em>
        </h2>
        <p>
          Your customer data, your team’s knowledge, your everyday tools.
          Connect them to Garden and build apps that put it all to work—for
          everyone.
        </p>
        <p className="g-integration-note">
          Shared apps use the accounts you connect. Your team and its agents
          build on the same context and capabilities.
        </p>
      </div>
      <div
        className="g-integration-map"
        aria-label="Stripe, HubSpot, Notion, GitHub, and Gmail connect to shared Garden apps"
      >
        <div className="g-connected-tools">
          {(
            [
              ["stripe", "Stripe"],
              ["hubspot", "HubSpot"],
              ["notion", "Notion"],
              ["github", "GitHub"],
              ["gmail", "Gmail"],
            ] as const
          ).map(([brand, label], index) => (
            <span
              key={brand}
              style={{ "--tool-index": index } as CSSProperties}
            >
              <BrandIcon name={brand} />
              {label}
            </span>
          ))}
          <span className="g-more-tools">+ 3,000</span>
        </div>
        <div className="g-integration-roots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="g-shared-capability">
          <Icon name="sprout" />
          <strong>Your team’s Garden</strong>
          <span>Shared context · Connected tools · Useful apps</span>
          <div>
            <Icon name="people" /> The whole team grows from here.
          </div>
        </div>
      </div>
    </section>
  );
}

function AgentConnections() {
  const { ref, visible, visited } = useInView<HTMLDivElement>();
  return (
    <div
      className="g-agent-diagram"
      ref={ref}
      data-visible={visible}
      data-visited={visited}
      aria-label="Your team and compatible agents connect to one shared Garden workspace through MCP"
    >
      <div className="g-agent-nodes">
        <span>
          <BrandIcon name="claude" /> Claude Code
        </span>
        <span>
          <BrandIcon name="chatgpt" /> ChatGPT
        </span>
        <span>
          <Icon name="plus" /> Your agent
        </span>
      </div>
      <div className="g-connection-lines" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <span className="g-mcp-label">
        MCP · ONE CONNECTION TO SHARED CONTEXT
      </span>
      <div className="g-agent-home">
        <Icon name="sprout" />
        <strong>Garden</strong>
        <span>Documents · Skills · Tools · Apps</span>
        <div className="g-team-pills">
          <span>
            <Icon name="people" /> Your team
          </span>
          <span>
            <Icon name="sprout" /> Built-in agent
          </span>
        </div>
      </div>
    </div>
  );
}

export function GardenPage() {
  const hero = useInView<HTMLElement>();
  useEffect(() => {
    document.title = "Garden by Sauna — Different agents. Common ground.";
  }, []);
  return (
    <div className="garden-site">
      <a className="g-skip-link" href="#main">
        Skip to content
      </a>
      <Header />
      <main id="main" className="g-main">
        <section
          className="g-hero g-container"
          aria-labelledby="hero-title"
          ref={hero.ref}
          data-visible={hero.visible}
        >
          <div className="g-hero-copy">
            <span className="g-eyebrow">
              <span className="g-alpha-tag">ALPHA</span> AN EXPERIMENT IN
              GROWING TOGETHER
            </span>
            <h1 id="hero-title">
              Different agents.
              <br />
              <em>Common ground.</em>
            </h1>
            <p className="g-hero-subtitle">
              Shared team context for all your AI agents.
            </p>
            <p className="g-hero-description">
              Bring your team’s documents, skills, and tools into one
              collaborative workspace—where people and agents build together.
            </p>
            <div className="g-hero-actions">
              <a className="g-button" href="/app">
                Try the free alpha <Icon name="arrow" />
              </a>
              <a className="g-explore-link" href="#garden">
                <span>
                  <Icon name="play" />
                </span>{" "}
                Explore the garden
              </a>
            </div>
            <AlphaNotice />
          </div>
          <div className="g-hero-specimen" aria-hidden="true">
            <Botanical lazy={false} />
            <span className="g-specimen-caption">
              Allium ursinum
              <br />
              <i>
                Different roots.
                <br />A shared place to grow.
              </i>
            </span>
            <span className="g-specimen-number">FIG. 01 — COMMON GROUND</span>
          </div>
        </section>
        <section
          className="g-container g-explorer-section"
          aria-label="Explore Garden"
        >
          <GardenExplorer />
        </section>
        <section
          className="g-collaboration g-container"
          id="together"
          aria-labelledby="together-title"
        >
          <div className="g-section-heading">
            <span className="g-eyebrow">GOOD THINGS GROW TOGETHER</span>
            <h2 id="together-title">
              Your team’s knowledge shouldn’t
              <br className="g-desktop-break" /> stop at a chat window.
            </h2>
            <p>
              Your team is using more agents. More of what it knows is getting
              trapped in private conversations. Garden gives teammates and their
              agents a shared place to contribute, collaborate, and pick up
              where someone else left off.
            </p>
          </div>
          <div className="g-use-cases">
            {(
              [
                [
                  "page",
                  "Shared project briefs",
                  "Keep your team and their agents working from the same goals, decisions, and latest updates. Less context drift. More progress.",
                  "Different agents. One direction.",
                ],
                [
                  "branch",
                  "Team playbooks",
                  "Share your best skills, tools, and hard-won know-how. Give everyone the benefit of your strongest people’s expertise.",
                  "Your best becomes everyone’s better.",
                ],
                [
                  "people",
                  "Meeting context",
                  "Turn conversations into shared learning. Keep the decisions, the why, and the next steps where every teammate and agent can build on them.",
                  "Learn once. Move forward together.",
                ],
              ] as [IconName, string, string, string][]
            ).map(([icon, title, description, note], index) => (
              <article key={title}>
                <div className="g-use-case-number">
                  <Icon name={icon} />
                  <span>0{index + 1}</span>
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
                <span className="g-use-case-note">{note}</span>
              </article>
            ))}
          </div>
        </section>
        <IntegrationsSection />
        <AppsSection />
        <section
          className="g-agents-section g-container"
          id="agents"
          aria-labelledby="agents-title"
        >
          <div className="g-agents-copy">
            <span className="g-eyebrow">YOUR AGENTS. YOUR CHOICE.</span>
            <h2 id="agents-title">
              Whatever your team uses,
              <br />
              <em>meet here.</em>
            </h2>
            <p>
              Connect Claude Code, ChatGPT, Codex, or another compatible agent
              through MCP. Use your existing agent subscription to work with
              shared pages and tools—or get started with Garden’s built-in
              agent.
            </p>
            <p className="g-mcp-explanation">
              MCP is the connection that lets your agent read and contribute to
              your shared workspace. Your team sees rich pages; your agents read
              and edit Markdown.
            </p>
            <a className="g-text-link" href="/docs/mcp">
              Bring your agent <Icon name="arrow" />
            </a>
          </div>
          <AgentConnections />
          <div className="g-coming-soon">
            <span className="g-coming-label">COMING SOON</span>
            <span>
              <BrandIcon name="slack" /> Slack
            </span>
            <span>
              <BrandIcon name="imessage" /> iMessage
            </span>
            <span>
              <Icon name="mail" /> Email
            </span>
            <p>Talk to Garden in more of the places your team already works.</p>
          </div>
        </section>
        <section className="g-closing" aria-labelledby="closing-title">
          <div className="g-container">
            <Botanical className="g-closing-flower" />
            <span className="g-eyebrow">
              A LITTLE CONTEXT. A LOT OF POSSIBILITY.
            </span>
            <h2 id="closing-title">
              Grow something <em>together.</em>
            </h2>
            <p>
              Garden is free to explore during the public alpha. Our aim is to
              keep shared context free or inexpensive, with the option to bring
              your own agent.
            </p>
            <a className="g-button" href="/app">
              Try the free alpha <Icon name="arrow" />
            </a>
            <AlphaNotice />
            <a className="g-docs-link" href="/docs">
              Read the docs <Icon name="arrow" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
